<script setup>
import { ref, computed, onMounted } from 'vue';
import { useLocale } from 'forms/composables/useLocale';
import FormField from 'forms/components/FormField.vue';

const { t } = useLocale();

const props = defineProps({
  field: { type: Object, required: true },
  // Flat map of member storage name -> current value (shared with FormPage).
  values: { type: Object, default: () => ({}) },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update']);

const showAddMenu = ref(false);
// Member storage names the visitor has chosen to fill.
const activeNames = ref(new Set());

const members = computed(() => props.field.members || []);

const activeMembers = computed(() =>
  members.value.filter(m => activeNames.value.has(m.name))
);

const inactiveMembers = computed(() =>
  members.value.filter(m => !activeNames.value.has(m.name))
);

// Build the field object FormField expects from a group member.
function memberField(member) {
  return {
    type: member.field_type || 'text',
    name: member.name,
    label: member.label,
    placeholder: member.placeholder || '',
    required: false,
  };
}

function activate(member) {
  activeNames.value = new Set(activeNames.value).add(member.name);
  showAddMenu.value = false;
}

function deactivate(member) {
  const next = new Set(activeNames.value);
  next.delete(member.name);
  activeNames.value = next;
  emit('update', member.name, '');
}

function onMemberInput(member, value) {
  emit('update', member.name, value);
}

onMounted(() => {
  // Auto-activate any member already carrying a value (e.g. URL prefill).
  const prefilled = members.value
    .filter(m => {
      const v = props.values[m.name];
      return v !== undefined && v !== null && String(v) !== '';
    })
    .map(m => m.name);
  if (prefilled.length) activeNames.value = new Set(prefilled);
});
</script>

<template>
  <div>
    <label class="block text-sm font-medium text-slate-700 mb-1.5">
      {{ field.label }}
    </label>

    <!-- Active member inputs -->
    <div v-if="activeMembers.length" class="space-y-3 mb-2">
      <div
        v-for="member in activeMembers"
        :key="member.name"
        class="flex items-end gap-2"
      >
        <div class="flex-1 min-w-0">
          <FormField
            :field="memberField(member)"
            :model-value="values[member.name]"
            :error="errors[member.name]"
            @update:model-value="onMemberInput(member, $event)"
          />
        </div>
        <button
          type="button"
          class="mb-1 p-2 text-slate-400 hover:text-red-500 transition-colors shrink-0"
          :title="t('GROUP_REMOVE')"
          @click="deactivate(member)"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Add picker -->
    <div v-if="inactiveMembers.length" class="relative inline-block">
      <button
        type="button"
        class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-woot-600 bg-woot-50 hover:bg-woot-100 rounded-lg transition-colors"
        @click="showAddMenu = !showAddMenu"
      >
        <svg
          class="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        {{ activeMembers.length ? t('GROUP_ADD_ANOTHER') : t('GROUP_ADD') }}
      </button>

      <div
        v-if="showAddMenu"
        class="absolute z-10 mt-1 w-52 rounded-lg border border-slate-200 bg-white shadow-lg py-1"
      >
        <button
          v-for="member in inactiveMembers"
          :key="member.name"
          type="button"
          class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"
          @click="activate(member)"
        >
          <svg
            v-if="member.icon"
            class="w-4 h-4 text-slate-400 shrink-0"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path :d="member.icon" />
          </svg>
          {{ member.label }}
        </button>
      </div>
    </div>
  </div>
</template>
